// Claude Code integration: the logic behind the two hooks that `npx ovid init`
// wires into a project's .claude/settings.json.
//
//   SessionStart   -> `ovid claude-hook session-start` prints a short workflow
//                     pointer to stdout, which Claude Code injects as context.
//   PreToolUse/Bash -> `ovid claude-hook pre-tool-use` reads the tool call from
//                     stdin and DENIES a raw `gh pr create`, redirecting the agent
//                     to `npx ovid publish` (so the e2e video always lands on the PR).
//
// The text and decision logic live here (single source of truth, unit-tested);
// cli.ts is a thin wrapper that reads stdin and prints stdout.
import { existsSync } from "node:fs";
import { join } from "node:path";

/** Matches a `gh pr create` anywhere in a (possibly compound) bash command. */
export const GH_PR_CREATE_RE = /\bgh\s+pr\s+create\b/;

/** Short, always-injected pointer (SessionStart). The Skill carries the detail. */
export const SESSION_START_POINTER = [
  "This project uses **ovid** for e2e test videos (terminal + browser).",
  "- When you implement or change a feature, verify it with an ovid e2e test: write/update a spec in",
  "  ./ovid/*.spec.ts (see ./ovid/WRITING-OVID-E2E-TESTS.md for the API), then run `npx ovid test`.",
  "  Fix the code or the spec until it passes — assertions decide pass/fail; the video is for humans.",
  "- To open a pull request for code changes, use `npx ovid publish` — do NOT use `gh pr create`.",
  "  See the `ovid` skill for the exact review-then-publish steps.",
].join("\n");

/** Reason surfaced to the agent when we deny `gh pr create` (PreToolUse). */
export const PR_BLOCK_REASON = [
  "Don't open the PR with `gh pr create`. Use ovid so the e2e video is attached to the PR:",
  "1. Run `npx ovid publish` (no args). It prints the suggested specs (new AND modified) and, per step,",
  "   a keyframe image path.",
  "2. READ those keyframe images to confirm what each step actually produced.",
  "3. Write a claims JSON file shaped like",
  '   { "<spec title>": { "summary": "…", "steps": ["…", "…"] } }',
  "   (grounded in the keyframes), then run:",
  '   npx ovid publish --apply --claims <file> --feature "<spec title>" --pr-title "<title>" --pr-summary "<description>"',
  "New specs are auto-featured; a MODIFIED spec gets a video only if you pass it with --feature.",
  "Repeat --feature per changed spec. This runs the tests, attaches the videos, and opens the PR.",
].join("\n");

/** Is `projectRoot` an ovid-initialized project? */
export function isOvidProject(projectRoot: string): boolean {
  return existsSync(join(projectRoot, "ovid.config.ts")) || existsSync(join(projectRoot, "ovid"));
}

/** SessionStart stdout: the pointer for an ovid project, else "" (inject nothing). */
export function sessionStartContext(projectRoot: string): string {
  return isOvidProject(projectRoot) ? SESSION_START_POINTER : "";
}

/** The `deny` payload Claude Code expects on stdout, or null to allow (stay silent). */
export interface PreToolUseDeny {
  hookSpecificOutput: {
    hookEventName: "PreToolUse";
    permissionDecision: "deny";
    permissionDecisionReason: string;
  };
}

/** PreToolUse decision for a bash command: deny `gh pr create`, else allow (null). */
export function preToolUseDecision(command: string): PreToolUseDeny | null {
  if (!GH_PR_CREATE_RE.test(command)) return null;
  return {
    hookSpecificOutput: {
      hookEventName: "PreToolUse",
      permissionDecision: "deny",
      permissionDecisionReason: PR_BLOCK_REASON,
    },
  };
}

/** Extract the bash command from a PreToolUse hook stdin payload; "" if absent/invalid. */
export function commandFromHookInput(raw: string): string {
  try {
    const parsed = JSON.parse(raw) as { tool_input?: { command?: unknown } };
    const cmd = parsed?.tool_input?.command;
    return typeof cmd === "string" ? cmd : "";
  } catch {
    return "";
  }
}
